import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, WMSTileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X, MapPin, Tent, Car, Waves, TreePine, Star, Download } from 'lucide-react';
import { CampingSpot, Coordinates, VehicleCategory } from '../../types';
import { getVehicleMarkerHtml, ensureVehicleMarkerStyles } from '../../utils/vehicleMarkerHtml';
import { useTranslation } from 'react-i18next';

const SATELLITE_TILE = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const FOREST_ROADS_WMS = 'https://wms.geonorge.no/skwms1/wms.traktorveg_skogsbilveger?';

const getSpotIcon = (spot: CampingSpot) => {
  if (spot.spotType === 'rasteplass') return <Car size={16} className="text-blue-400" />;
  if (spot.spotType === 'commercial_campsite') return <Tent size={16} className="text-amber-400" />;
  if (spot.spotType === 'coastal') return <Waves size={16} className="text-cyan-400" />;
  if (spot.features?.includes('GAPAHUK')) return <TreePine size={16} className="text-emerald-400" />;
  return <MapPin size={16} className="text-emerald-400" />;
};

const getScoreColor = (score: number) => {
  if (score >= 8) return 'text-emerald-400';
  if (score >= 4) return 'text-amber-400';
  return 'text-red-400';
};

// Custom spot marker icon (red pulsing)
const spotMarkerIcon = L.divIcon({
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  html: `<div style="position:relative;width:32px;height:32px">
    <div style="position:absolute;inset:-8px;border-radius:50%;background:rgba(239,68,68,0.2);animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite"></div>
    <div style="position:absolute;inset:-4px;border-radius:50%;background:rgba(239,68,68,0.12);border:2px solid rgba(248,113,113,0.4)"></div>
    <div style="width:32px;height:32px;border-radius:50%;background:#ef4444;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
    </div>
  </div>
  <style>@keyframes ping{75%,100%{transform:scale(2);opacity:0}}</style>`,
});

// User position marker (blinking car)
const userCarIcon = L.divIcon({
  className: '',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  html: `<div class="spot-preview-user-car" style="width:36px;height:36px;display:flex;align-items:center;justify-content:center">
    <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.25);animation:spot-car-blink 1.2s ease-in-out infinite"></div>
    <div style="width:28px;height:28px;border-radius:50%;background:#3b82f6;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>
    </div>
  </div>
  <style>@keyframes spot-car-blink{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.4;transform:scale(1.3)}}</style>`,
});

// Helper to invalidate map size after container renders
const MapInvalidator: React.FC = () => {
  const { t } = useTranslation();
  const map = useMap();
  React.useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 100);
    return () => clearTimeout(t);
  }, [map]);
  return null;
};

// Offset center to compensate for info bar overlapping bottom of map
const CenterOffset: React.FC<{ lat: number; lon: number }> = ({ lat, lon }) => {
  const map = useMap();
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const containerHeight = map.getSize().y;
      if (containerHeight === 0) return;
      const point = map.latLngToContainerPoint([lat, lon]);
      const adjustedPoint = L.point(point.x, point.y + 30);
      const adjustedLatLng = map.containerPointToLatLng(adjustedPoint);
      map.setView(adjustedLatLng, map.getZoom(), { animate: false });
    }, 200);
    return () => clearTimeout(timer);
  }, [map, lat, lon]);
  return null;
};

interface SpotPreviewOverlayProps {
  spot: CampingSpot;
  distanceM?: number;
  onClose: () => void;
  onDetails: (id: string) => void;
  userPosition?: Coordinates | null;
  onDownloadArea?: (id: string) => void;
  vehicleMarkerEnabled?: boolean;
  vehicleMarkerCategory?: VehicleCategory;
  vehicleMarkerPhotoUrl?: string;
  vehicleMarkerAiAvatarSvg?: string;
}

const SpotPreviewOverlay: React.FC<SpotPreviewOverlayProps> = ({ spot, distanceM, onClose, onDetails, userPosition, onDownloadArea, vehicleMarkerEnabled, vehicleMarkerCategory, vehicleMarkerPhotoUrl, vehicleMarkerAiAvatarSvg }) => {
  const { t } = useTranslation();
  const lat = spot.location.latitude;
  const lon = spot.location.longitude;

  const formatDist = (m: number) => m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;

  // Vehicle avatar marker (or fallback to default car icon)
  const userMarkerIcon = useMemo(() => {
    if (vehicleMarkerEnabled && vehicleMarkerCategory) {
      ensureVehicleMarkerStyles();
      return L.divIcon({
        className: '',
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        html: getVehicleMarkerHtml({
          category: vehicleMarkerCategory,
          photoUrl: vehicleMarkerPhotoUrl,
          aiAvatarSvg: vehicleMarkerAiAvatarSvg,
          borderColor: '#3b82f6',
          pulseColor: 'rgba(59,130,246,0.25)',
          size: 36,
        }),
      });
    }
    return userCarIcon;
  }, [vehicleMarkerEnabled, vehicleMarkerCategory, vehicleMarkerPhotoUrl, vehicleMarkerAiAvatarSvg]);

  // Auto-download countdown
  const [countdown, setCountdown] = useState(onDownloadArea ? 5 : -1);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!onDownloadArea || countdown <= 0) return;
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger download when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && !downloadStarted && onDownloadArea) {
      setDownloadStarted(true);
      onDownloadArea(spot.id);
    }
  }, [countdown, downloadStarted, onDownloadArea, spot.id]);

  const cancelCountdown = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(-1);
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20"
        style={{ width: 'min(90vw, 768px)', maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Interactive satellite map */}
        <div className="relative" style={{ width: '100%', aspectRatio: '1/1' }}>
          <MapContainer
            center={[lat, lon]}
            zoom={15}
            minZoom={12}
            maxZoom={18}
            zoomControl={false}
            attributionControl={false}
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
          >
            <MapInvalidator />
            <CenterOffset lat={lat} lon={lon} />
            <TileLayer
              url={SATELLITE_TILE}
              maxZoom={18}
            />
            <WMSTileLayer
              url={FOREST_ROADS_WMS}
              layers="skogsbilveg,traktorveg_sti"
              format="image/png"
              transparent={true}
              opacity={0.8}
            />
            {/* Spot marker */}
            <Marker position={[lat, lon]} icon={spotMarkerIcon} zIndexOffset={1000} />
            {/* User position marker with car icon */}
            {userPosition && (
              <Marker position={[userPosition.latitude, userPosition.longitude]} icon={userMarkerIcon} />
            )}
          </MapContainer>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-[1000] w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-all active:scale-90"
          >
            <X size={18} />
          </button>
        </div>

        {/* Info bar */}
        <div className="bg-gradient-to-t from-black/90 to-black/60 px-4 py-3">
          <div className="flex items-center gap-2">
            {getSpotIcon(spot)}
            <span className="text-white text-sm font-bold truncate flex-1">
              {spot.name || 'Ukjent sted'}
            </span>
            {spot.score != null && (
              <div className="flex items-center gap-1">
                <Star size={12} fill="currentColor" className={getScoreColor(spot.score)} />
                <span className={`text-sm font-black ${getScoreColor(spot.score)}`}>{spot.score.toFixed(1)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              {distanceM != null && (
                <span className="text-white/70 text-xs font-bold">{formatDist(distanceM)} unna</span>
              )}
              {/* Auto-download countdown */}
              {onDownloadArea && countdown > 0 && (
                <button
                  onClick={cancelCountdown}
                  className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 text-2xs font-bold hover:bg-emerald-500/30 transition-all flex items-center gap-1"
                >
                  <Download size={10} /> Laster ned om {countdown}s… <X size={8} />
                </button>
              )}
              {onDownloadArea && downloadStarted && (
                <span className="text-emerald-300 text-2xs font-bold flex items-center gap-1">
                  <Download size={10} /> {t("auto.map.spotpreviewoverlay.laster_ned", "Laster ned…")}
                </span>
              )}
              {onDownloadArea && countdown === -1 && !downloadStarted && (
                <button
                  onClick={() => {
                    onDownloadArea(spot.id);
                  }}
                  className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 text-2xs font-bold hover:bg-emerald-500/30 transition-all flex items-center gap-1"
                >
                  <Download size={10} /> {t("auto.map.spotpreviewoverlay.last_ned", "Last ned")}
                </button>
              )}
            </div>
            <button
              onClick={() => onDetails(spot.id)}
              className="px-4 py-1.5 rounded-xl bg-white/20 text-white text-xs font-black hover:bg-white/30 transition-all active:scale-95"
            >
              {t("auto.map.spotpreviewoverlay.detaljer", "Detaljer")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotPreviewOverlay;
